import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { LocalStorageService } from "../../../core/core";
import { HttpClient } from "@angular/common/http";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";

@Component({
  selector: "common-search-results-nearby-locations",
  templateUrl: "./search-results-nearby-locations.component.html",
  styleUrls: ["./search-results-nearby-locations.component.scss"],
})
export class SearchResultsNearbyLocationsComponent implements OnInit, OnDestroy {
  primaryType: string;
  cityState: string;
  baseRoute: string = "equipment-rentals";
  collectionLabel: string = "rentals";
  collectionLabelTitleCase: string = "Rentals";

  nearbySorted: any;
  currentCity: any;
  localStorageCityData: any;

  private navigationSub: Subscription;
  private cachedCities: any[] = null;

  constructor(private route: ActivatedRoute, private localStorageService: LocalStorageService, private httpClient: HttpClient, private router: Router) {}

  ngOnInit(): void {
    this.loadNearbyLocations();

    this.navigationSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.loadNearbyLocations());
  }

  ngOnDestroy(): void {
    this.navigationSub?.unsubscribe();
  }

  private loadNearbyLocations(): void {
    this.baseRoute = this.getBaseRoute();
    this.applyRouteContext();

    const deepestRoute = this.getDeepestChild(this.route);
    this.primaryType = deepestRoute?.snapshot.paramMap.get("primaryType") || null;
    this.cityState = deepestRoute?.snapshot.paramMap.get("cityState") || "";

    if (this.cityState == "") {
      this.nearbySorted = null;
      return;
    }

    this.localStorageCityData = this.localStorageService.get(`location-${this.cityState}`);

    if (this.cachedCities) {
      this.getNearbyCities(this.cachedCities);
      return;
    }

    this.httpClient.get("https://company.rubbl.com/pubweb/json/location/types-cities-live-inventory.json").subscribe((response: any) => {
      this.cachedCities = response;
      this.getNearbyCities(response);
    });
  }

  getNearbyCities(cities) {
    const split = this.cityState.split("-");
    const userState = split[split.length - 1].trim();
    const userCity = split.slice(0, -1).join(" ").trim();
    this.cityState = `${userCity}, ${userState}`; // Update cityState for display

    // Step 2: Find matching city entry in your dataset
    const currentCity = cities.find(c => c.name === userCity && c.state === userState);
    this.currentCity = currentCity;
    // Step 3: If found, bucket cities by floored lat/lon to find spatially close ones

    let userLat = currentCity?.latitude;
    let userLon = currentCity?.longitude;

    if (!userLat || !userLon) {
      userLat = this.localStorageCityData?.latitude;
      userLon = this.localStorageCityData?.longitude;
    }

    if (userLat && userLon) {
      const latBucket = Math.floor(userLat);
      const lonBucket = Math.floor(userLon);

      // Step 4: Filter cities in the same or adjacent lat/lon buckets
      const possibleNearby = cities.filter(c => {
        const lat = Math.floor(c.latitude);
        const lon = Math.floor(c.longitude);
        return (
          Math.abs(lat - latBucket) <= 2 && Math.abs(lon - lonBucket) <= 2 && !(c.name === userCity && c.state === userState) // exclude self and expanded range
        );
      });

      // Optional: Sort by raw distance if desired
      const withDistances = possibleNearby.map(c => {
        // const [lat2, lon2] = c.coordinates;
        const distance = this.haversine(userLat, userLon, c.latitude, c.longitude);
        return { ...c, distance };
      });

      // Sort by distance and return top N
      const nearbySorted = withDistances.sort((a, b) => a.distance - b.distance).slice(0, 5);

      nearbySorted.forEach(city => {
        city.searchResultsCity = `${city.name.split(" ").join("-")}-${city.state}`; // Update cityState for display
      });

      this.nearbySorted = nearbySorted;
    }
  }

  routerLinkForCity(city: string): string[] {
    return ["/" + this.baseRoute, this.primaryType, city];
  }

  private applyRouteContext() {
    switch (this.baseRoute) {
      case "equipment-for-sale":
        this.collectionLabel = "machines for sale";
        break;
      default:
        // Check for rpoEnabled query param
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('rpoEnabled') === 'true') {
          this.collectionLabel = "rent-to-own machines";
        } else {
          this.collectionLabel = "rentals";
        }
        break;
    }

    this.collectionLabelTitleCase = this.collectionLabel
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  private getBaseRoute(): string {
    const segments = this.router.url.split("?")[0].split("/").filter(Boolean);
    if (!segments.length) {
      return "equipment-rentals";
    }

    if (segments[0] === "map") {
      return "equipment-rentals";
    }

    return segments[0];
  }

  private getDeepestChild(route: ActivatedRoute): ActivatedRoute | null {
    let current: ActivatedRoute | null = route;
    while (current?.firstChild) {
      current = current.firstChild;
    }
    return current;
  }

  haversine(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth radius in km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }
}
